import React, { useState, useEffect, useCallback } from "react";
import {
  Accordion,
  AccordionItem,
  Button,
  FormField,
  NumberInput,
  Rows,
  SegmentedControl,
  Select,
  Slider,
  Switch,
  Text,
} from "@canva/app-ui-kit";

export type VariancePeriod = "month" | "qtd" | "ytd";

/**
 * Mirrors `VarianceSectionConfig.comparisonType` on the platform side.
 * `scenario_vs_scenario` is omitted here — it requires picking base+compare
 * scenarios and is deferred until we add a scenario picker to the Canva app.
 */
export type VarianceComparison = "forecast_vs_actuals" | "actuals_vs_previous_period";

export interface VarianceConfig {
  period: VariancePeriod;
  thresholdPercent: number;
  showOnlyOverThreshold: boolean;
  comparison: VarianceComparison;
  maxRows: number;
  summaryOnly: boolean;
}

export const DEFAULT_VARIANCE_CONFIG: VarianceConfig = {
  period: "month",
  thresholdPercent: 10,
  showOnlyOverThreshold: false,
  comparison: "forecast_vs_actuals",
  maxRows: 15,
  summaryOnly: false,
};

const STORAGE_KEY_PREFIX = "profitual:variance-config:";

function loadConfig(scenarioId: string): VarianceConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PREFIX + scenarioId);
    if (!raw) return DEFAULT_VARIANCE_CONFIG;
    return { ...DEFAULT_VARIANCE_CONFIG, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_VARIANCE_CONFIG;
  }
}

function saveConfig(scenarioId: string, config: VarianceConfig) {
  try {
    localStorage.setItem(STORAGE_KEY_PREFIX + scenarioId, JSON.stringify(config));
  } catch {
    // localStorage may be unavailable in the Canva iframe; non-fatal
  }
}

interface VarianceConfigCardProps {
  scenarioId: string;
  onInsert: (config: VarianceConfig) => Promise<void>;
  loading?: boolean;
  error?: string | null;
  warning?: string | null;
}

export function VarianceConfigCard({
  scenarioId,
  onInsert,
  loading,
  error,
  warning,
}: VarianceConfigCardProps) {
  const [config, setConfig] = useState<VarianceConfig>(() => loadConfig(scenarioId));

  // Reload config when the scenario changes
  useEffect(() => {
    setConfig(loadConfig(scenarioId));
  }, [scenarioId]);

  // Persist on change
  useEffect(() => {
    saveConfig(scenarioId, config);
  }, [scenarioId, config]);

  const update = useCallback(
    <K extends keyof VarianceConfig>(key: K, value: VarianceConfig[K]) => {
      setConfig((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  return (
    <Rows spacing="1.5u">
      <FormField
          label="Period"
          value={config.period}
          control={(props) => (
            <SegmentedControl<VariancePeriod>
              {...props}
              options={[
                { value: "month", label: "Month" },
                { value: "qtd", label: "QTD" },
                { value: "ytd", label: "YTD" },
              ]}
              onChange={(v) => update("period", v)}
            />
          )}
        />

        <FormField
          label={`Highlight threshold: ±${config.thresholdPercent}%`}
          value={config.thresholdPercent}
          control={(props) => (
            <Slider
              {...props}
              min={0}
              max={100}
              step={1}
              onChange={(v) => update("thresholdPercent", v)}
            />
          )}
        />

        <Switch
          label="Only show rows over threshold"
          description="Hide line items within the threshold band"
          value={config.showOnlyOverThreshold}
          onChange={(v) => update("showOnlyOverThreshold", v)}
        />

        <Switch
          label="Summary only"
          description="Skip individual lines — show only section totals"
          value={config.summaryOnly}
          onChange={(v) => update("summaryOnly", v)}
        />

        <Accordion>
          <AccordionItem title="Advanced">
            <Rows spacing="1u">
              <FormField
                label="Comparison"
                value={config.comparison}
                control={(props) => (
                  <Select<VarianceComparison>
                    {...props}
                    options={[
                      { value: "forecast_vs_actuals", label: "Forecast vs Actuals" },
                      { value: "actuals_vs_previous_period", label: "Period over Period" },
                    ]}
                    onChange={(v) => update("comparison", v)}
                    stretch
                  />
                )}
              />

              <FormField
                label="Max rows on slide"
                value={config.maxRows}
                control={(props) => (
                  <NumberInput
                    {...props}
                    min={5}
                    max={30}
                    step={1}
                    hasSpinButtons
                    incrementAriaLabel="Increase max rows"
                    decrementAriaLabel="Decrease max rows"
                    onChange={(v) =>
                      update("maxRows", typeof v === "number" && !isNaN(v) ? v : 15)
                    }
                  />
                )}
              />
            </Rows>
          </AccordionItem>
        </Accordion>

        {error && (
          <Text size="small" tone="critical">
            {error}
          </Text>
        )}
        {warning && !error && (
          <Text size="small" tone="tertiary">
            {warning}
          </Text>
        )}

        <Button
          variant="secondary"
          onClick={() => onInsert(config)}
          loading={loading}
          stretch
        >
        Insert Variance Slide
      </Button>
    </Rows>
  );
}
